'use client';
import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';

const ISLAMIC_SUBJECTS = [
  { id: 'quran', name: "Qur\'an Studies", nameAr: 'علوم القرآن', emoji: '📖', color: 'from-teal-500 to-cyan-600', desc: 'Recitation, Tafsir themes, memorization support, and Qur\'anic vocabulary', lessons: 120, levels: 8 },
  { id: 'arabic', name: 'Arabic Language', nameAr: 'اللغة العربية', emoji: '🔤', color: 'from-emerald-500 to-green-600', desc: 'Complete Arabic acquisition from alphabet to classical Qur\'anic Arabic and scholarly texts', lessons: 160, levels: 8 },
  { id: 'tajweed', name: 'Tajweed', nameAr: 'التجويد', emoji: '🎵', color: 'from-amber-500 to-yellow-600', desc: 'Tajweed rules, pronunciation, makharij, and color-coded recitation training', lessons: 80, levels: 6 },
  { id: 'hifdh', name: 'Hifdh Support', nameAr: 'الحفظ', emoji: '🌙', color: 'from-indigo-500 to-violet-600', desc: 'Memorization tracking, revision schedules, and Hifdh progress dashboards', lessons: 60, levels: 5 },
  { id: 'aqidah', name: 'Islamic Theology (Aqidah)', nameAr: 'العقيدة', emoji: '🕌', color: 'from-green-600 to-teal-700', desc: 'Foundations of Islamic belief, Tawhid, pillars of faith, and theological reasoning', lessons: 90, levels: 7 },
  { id: 'seerah', name: 'Seerah', nameAr: 'السيرة النبوية', emoji: '🌟', color: 'from-rose-500 to-pink-600', desc: 'Life of Prophet Muhammad ﷺ from birth to the completion of the message', lessons: 100, levels: 7 },
  { id: 'hadith', name: 'Hadith Studies', nameAr: 'علوم الحديث', emoji: '📜', color: 'from-orange-500 to-amber-600', desc: 'Hadith sciences, major collections, authentication, and application', lessons: 85, levels: 6 },
  { id: 'fiqh', name: 'Fiqh Foundations', nameAr: 'الفقه', emoji: '⚖️', color: 'from-sky-500 to-blue-600', desc: 'Islamic jurisprudence basics, pillars of worship, halal/haram, and contemporary issues', lessons: 95, levels: 7 },
  { id: 'islamic_civ', name: 'Islamic Civilization', nameAr: 'الحضارة الإسلامية', emoji: '🏛️', color: 'from-violet-500 to-purple-600', desc: 'Golden Age of Islam, Muslim scientists, philosophers, and global contributions', lessons: 75, levels: 6 },
  { id: 'islamic_ethics', name: 'Islamic Ethics & Adab', nameAr: 'الأخلاق والآداب', emoji: '💚', color: 'from-lime-500 to-green-600', desc: 'Character development, adab, ihsan, amanah, and ethical decision-making', lessons: 80, levels: 8 },
  { id: 'comparative_religion', name: 'Comparative Religion', nameAr: 'مقارنة الأديان', emoji: '🌍', color: 'from-cyan-500 to-teal-600', desc: 'Awareness of world religions, interfaith dialogue, and Islamic perspective', lessons: 50, levels: 4 },
  { id: 'islamic_finance', name: 'Islamic Finance', nameAr: 'التمويل الإسلامي', emoji: '💰', color: 'from-yellow-500 to-amber-600', desc: 'Halal economics, riba prohibition, zakat, waqf, and Islamic banking principles', lessons: 60, levels: 5 },
];

const TAJWEED_RULES = [
  { rule: 'Ikhfa', nameAr: 'الإخفاء', desc: 'Concealment of noon sakinah or tanwin before 15 letters', color: 'tajweed-ikhfa', example: 'مِنْ قَبْلِ' },
  { rule: 'Idgham', nameAr: 'الإدغام', desc: 'Merging of noon sakinah into following letter', color: 'tajweed-idgham', example: 'مِنْ نَّعِيمٍ' },
  { rule: 'Qalqalah', nameAr: 'القلقلة', desc: 'Echo sound on letters ق ط ب ج د when sukoon', color: 'tajweed-qalqalah', example: 'يَقْطَعُونَ' },
  { rule: 'Madd', nameAr: 'المد', desc: 'Elongation of vowel sounds — 2, 4, or 6 counts', color: 'tajweed-madd', example: 'قَالَ' },
  { rule: 'Ghunnah', nameAr: 'الغنة', desc: 'Nasalization on meem and noon with shaddah', color: 'bg-rose-100 text-rose-800', example: 'إِنَّ' },
  { rule: 'Iqlab', nameAr: 'الإقلاب', desc: 'Conversion of noon sakinah to meem before ب', color: 'bg-purple-100 text-purple-800', example: 'مِنْ بَعْدِ' },
];

const ARABIC_LEVELS = [
  { level: 'Alphabet', desc: 'Letter recognition, shapes, and sounds', topics: ['28 Arabic letters', 'Letter forms (initial/medial/final)', 'Vowel marks (harakat)', 'Basic pronunciation'] },
  { level: 'Phonics & Words', desc: 'Connecting letters, basic vocabulary', topics: ['Letter connections', 'Common words', 'Short vowels', 'Tanwin'] },
  { level: 'Basic Grammar', desc: 'Nouns, verbs, and simple sentences', topics: ['Ism (noun)', 'Fi\'l (verb)', 'Harf (particle)', 'Gender in Arabic'] },
  { level: 'Sentence Structure', desc: 'Jumlah fi\'liyyah and jumlah ismiyyah', topics: ['Verbal sentences', 'Nominal sentences', 'Definite/indefinite', 'Plural forms'] },
  { level: 'Qur\'anic Vocabulary', desc: 'Most frequent Qur\'anic words and roots', topics: ['Root word system', '500 most common words', 'Qur\'anic context', 'Tafsir vocabulary'] },
  { level: 'Classical Arabic', desc: 'Grammar deep-dive and classical texts', topics: ['Nahw (syntax)', 'Sarf (morphology)', 'Classical literature', 'Scholarly terminology'] },
  { level: 'Advanced Rhetoric', desc: 'Balaghah and Qur\'anic eloquence', topics: ['Balaghah', 'Qur\'anic i\'jaz', 'Rhetorical devices', 'Tafsir methodology'] },
  { level: 'Scholarly Research', desc: 'Academic Arabic and research skills', topics: ['Academic writing', 'Usul al-Fiqh terminology', 'Maqasid framework', 'Research methodology'] },
];

const HIFDH_JUZAA = Array.from({ length: 30 }, (_, i) => ({
  juz: i + 1,
  name: `Juz' ${i + 1}`,
  status: i < 3 ? 'mastered' : i < 6 ? 'in_progress' : i < 8 ? 'started' : 'locked',
  percentage: i < 3 ? 100 : i < 6 ? [85, 60, 30][i - 3] : i < 8 ? [15, 5][i - 6] : 0,
}));

export default function IslamicEducationPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'arabic' | 'tajweed' | 'hifdh' | 'subjects'>('overview');

  const statusColor: Record<string, string> = {
    mastered: 'bg-primary text-white',
    in_progress: 'bg-amber-400 text-white',
    started: 'bg-sky-400 text-white',
    locked: 'bg-muted text-muted-foreground',
  };

  return (
    <AppLayout activeRoute="/islamic-education">
      <div className="space-y-8 animate-fade-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-emerald-700 rounded-2xl p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="text-5xl">🕌</div>
            <div>
              <h1 className="text-2xl font-800">Islamic Education Division</h1>
              <p className="text-teal-100 mt-1 text-sm">بِسْمِ اللَّهِ الرَّحْمَنِ الرَّحِيمِ — Comprehensive Islamic scholarship from early childhood to graduate level</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {['12 Islamic Subjects', '8 Developmental Levels', 'Authentic Sources', 'Multilingual'].map(tag => (
                  <span key={tag} className="text-xs bg-white/20 rounded-lg px-2 py-1 font-600">{tag}</span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 border-b border-border pb-4">
          {[
            { id: 'overview', label: 'Overview', emoji: '📋' },
            { id: 'arabic', label: 'Arabic Pathway', emoji: '🔤' },
            { id: 'tajweed', label: 'Tajweed', emoji: '🎵' },
            { id: 'hifdh', label: 'Hifdh Tracker', emoji: '🌙' },
            { id: 'subjects', label: 'All Subjects', emoji: '📚' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-600 transition-all ${activeTab === tab.id ? 'bg-primary text-white shadow-sm' : 'bg-card border border-border text-foreground hover:border-primary/40'}`}
            >
              <span>{tab.emoji}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Islamic Subjects', value: '12', emoji: '📚', color: 'bg-teal-50 border-teal-200' },
                { label: 'Total Lessons', value: '1,055+', emoji: '📝', color: 'bg-emerald-50 border-emerald-200' },
                { label: 'Languages Supported', value: '20+', emoji: '🌍', color: 'bg-amber-50 border-amber-200' },
                { label: 'Scholarly Levels', value: '8', emoji: '🎓', color: 'bg-indigo-50 border-indigo-200' },
              ].map(stat => (
                <div key={stat.label} className={`rounded-2xl p-4 border text-center ${stat.color}`}>
                  <div className="text-3xl mb-1">{stat.emoji}</div>
                  <p className="text-2xl font-800 text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {ISLAMIC_SUBJECTS.slice(0, 6).map(sub => (
                <div key={sub.id} className="bg-card rounded-2xl border border-border p-4 hover:shadow-md transition-all">
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${sub.color} flex items-center justify-center text-xl mb-3 shadow-sm`}>{sub.emoji}</div>
                  <h3 className="font-800 text-sm text-foreground">{sub.name}</h3>
                  <p className="text-xs text-muted-foreground rtl-text mt-0.5">{sub.nameAr}</p>
                  <p className="text-xs text-foreground/70 mt-2 leading-relaxed line-clamp-2">{sub.desc}</p>
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                    <span><span className="font-700 text-foreground">{sub.lessons}</span> lessons</span>
                    <span><span className="font-700 text-foreground">{sub.levels}</span> levels</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Arabic Pathway Tab */}
        {activeTab === 'arabic' && (
          <div className="space-y-6">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
              <h2 className="font-800 text-foreground mb-1">Complete Arabic Language Pathway</h2>
              <p className="text-sm text-muted-foreground">From alphabet recognition to scholarly-level classical Arabic and Qur'anic comprehension</p>
            </div>
            <div className="space-y-3">
              {ARABIC_LEVELS.map((level, idx) => (
                <div key={level.level} className="bg-card rounded-2xl border border-border p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center text-white font-800 text-sm flex-shrink-0 shadow-sm">
                      {idx + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-800 text-foreground">{level.level}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">{level.desc}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {level.topics.map(topic => (
                          <span key={topic} className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-lg font-500">{topic}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="bg-card rounded-2xl border border-border p-5">
              <h3 className="font-800 text-foreground mb-3">RTL Support & Accessibility</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {['Right-to-left text rendering', 'Arabic keyboard support', 'Tajweed color highlighting', 'Transliteration options', 'Audio pronunciation guides', 'Root word explorer'].map(feature => (
                  <div key={feature} className="flex items-center gap-2 text-sm text-foreground">
                    <span className="text-primary">✓</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Tajweed Tab */}
        {activeTab === 'tajweed' && (
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <h2 className="font-800 text-foreground mb-1">Tajweed Rules & Color Coding</h2>
              <p className="text-sm text-muted-foreground">Interactive tajweed learning with color-coded rules, pronunciation guides, and recitation practice</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {TAJWEED_RULES.map(rule => (
                <div key={rule.rule} className="bg-card rounded-2xl border border-border p-5">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <h3 className="font-800 text-foreground">{rule.rule}</h3>
                      <p className="text-sm text-muted-foreground rtl-text">{rule.nameAr}</p>
                    </div>
                    <span className={`text-lg px-3 py-1 rounded-xl font-700 rtl-text ${rule.color}`}>{rule.example}</span>
                  </div>
                  <p className="text-xs text-foreground/70 leading-relaxed">{rule.desc}</p>
                </div>
              ))}
            </div>
            <div className="bg-card rounded-2xl border border-border p-5">
              <h3 className="font-800 text-foreground mb-3">Tajweed Learning Features</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {['Color-coded Qur\'anic text', 'Audio recitation by certified Qari', 'Rule identification exercises', 'Pronunciation feedback system', 'Progressive rule introduction', 'Tajweed quiz challenges', 'Makharij (articulation points) guide', 'Recitation recording & playback'].map(f => (
                  <div key={f} className="flex items-center gap-2 text-sm text-foreground py-1">
                    <span className="text-teal">🎵</span>
                    <span>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Hifdh Tracker Tab */}
        {activeTab === 'hifdh' && (
          <div className="space-y-6">
            <div className="bg-indigo-50 border border-indigo-200 rounded-2xl p-5">
              <h2 className="font-800 text-foreground mb-1">Hifdh Progress Tracker</h2>
              <p className="text-sm text-muted-foreground">Track memorization of all 30 Juz' with revision schedules and mastery indicators</p>
            </div>
            <div className="grid grid-cols-5 sm:grid-cols-6 lg:grid-cols-10 gap-2">
              {HIFDH_JUZAA.map(juz => (
                <div key={juz.juz} className={`rounded-xl p-2 text-center cursor-pointer hover:scale-105 transition-transform ${statusColor[juz.status]}`}>
                  <p className="text-xs font-700">Juz'</p>
                  <p className="text-lg font-800">{juz.juz}</p>
                  {juz.percentage > 0 && <p className="text-[10px] font-600">{juz.percentage}%</p>}
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              {[
                { status: 'mastered', label: 'Mastered', color: 'bg-primary text-white' },
                { status: 'in_progress', label: 'In Progress', color: 'bg-amber-400 text-white' },
                { status: 'started', label: 'Started', color: 'bg-sky-400 text-white' },
                { status: 'locked', label: 'Not Started', color: 'bg-muted text-muted-foreground' },
              ].map(s => (
                <div key={s.status} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded ${s.color}`} />
                  <span className="text-xs text-muted-foreground">{s.label}</span>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                { label: 'Juz\' Memorized', value: '3', emoji: '🌙', color: 'bg-indigo-50 border-indigo-200' },
                { label: 'In Progress', value: '3', emoji: '📖', color: 'bg-amber-50 border-amber-200' },
                { label: 'Daily Revision', value: '2 pages', emoji: '🔄', color: 'bg-teal-50 border-teal-200' },
              ].map(stat => (
                <div key={stat.label} className={`rounded-2xl p-4 border text-center ${stat.color}`}>
                  <div className="text-2xl mb-1">{stat.emoji}</div>
                  <p className="text-xl font-800 text-foreground">{stat.value}</p>
                  <p className="text-xs text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Subjects Tab */}
        {activeTab === 'subjects' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ISLAMIC_SUBJECTS.map(sub => (
              <div key={sub.id} className="bg-card rounded-2xl border border-border p-5 hover:shadow-md transition-all">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${sub.color} flex items-center justify-center text-2xl mb-3 shadow-sm`}>{sub.emoji}</div>
                <h3 className="font-800 text-foreground">{sub.name}</h3>
                <p className="text-sm text-muted-foreground rtl-text mt-0.5">{sub.nameAr}</p>
                <p className="text-xs text-foreground/70 mt-2 leading-relaxed">{sub.desc}</p>
                <div className="flex items-center gap-3 mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
                  <span><span className="font-700 text-foreground">{sub.lessons}</span> lessons</span>
                  <span><span className="font-700 text-foreground">{sub.levels}</span> levels</span>
                  <button className="ml-auto text-xs text-primary font-600 hover:underline">Explore →</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
